import { Box } from '@mui/system';
import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutogiroMandateStatus } from 'src/modules/invoice/components/autogiroMandateStatus';
import { AutogiroMandateResponse } from 'src/modules/invoice/models/autogiroMandateResponse';
import { AutogiroSearchFilter } from 'src/modules/invoice/models/autogiroSearchFilter';
import SearchField from 'src/shared/components/form/searchField';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { getAllAutogiroMandate } from 'src/shared/services/autogiroMandateService';
import { AutogiroMandateConnectMemberRequest } from '../models/autogiroMandateConnectMemberRequest';

type AutogiroConnectMemberDataGridProps = {
  setOpenAddAutogiroDialog: (openAddAutogiroDialog: boolean) => void;
  setAutogiroMandateId: (autogiroMandateId: AutogiroMandateConnectMemberRequest) => void;
};

export const AutogiroConnectMemberDataGrid = (props: AutogiroConnectMemberDataGridProps) => {
  const { setOpenAddAutogiroDialog, setAutogiroMandateId } = props;
  const { t } = useTranslation();
  const { dateStamp } = useGlobals();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();

  const [rows, setRows] = useState<AutogiroMandateResponse[]>([]);
  const [autogiroMandateSearchFilter, setAutogiroMandateSearchFilter] = useState<AutogiroSearchFilter>({
    sortCol: 'firstName',
    sortDir: 'asc',
    pageIndex: 0,
    pageSize: 10,
    filter: '',
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: autogiroMandateSearchFilter.sortCol, sort: autogiroMandateSearchFilter.sortDir },
  ]);

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 15, 25, 50],
    pageSize: autogiroMandateSearchFilter.pageSize,
    page: autogiroMandateSearchFilter.pageIndex,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: autogiroMandateSearchFilter.pageIndex, pageSize: autogiroMandateSearchFilter.pageSize } }));
  }, [autogiroMandateSearchFilter.pageIndex, autogiroMandateSearchFilter.pageSize]);

  const onPageChange = (newPageIndex: number) => {
    setAutogiroMandateSearchFilter((prev) => ({ ...prev, pageIndex: newPageIndex }));

    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    setAutogiroMandateSearchFilter((prev) => ({ ...prev, pageIndex: 0, pageSize: newPageSize }));

    scrollIntoView();
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isMounted()) {
        updateData('loading', true);
      }
      try {
        const result = await getAllAutogiroMandate(autogiroMandateSearchFilter);
        if (isMounted()) {
          updateData('totalRows', result.data.totalItems);

          setTimeout(() => {
            setRows(
              result.data.results.map((item) => {
                return { ...item, id: item.autogiroMandate_id };
              }),
            );
          }, 100);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) {
          updateData('loading', false);
        }
      }
    };

    fetchData();
  }, [autogiroMandateSearchFilter, dateStamp, isMounted]);

  const onRowClick = (params) => {
    setAutogiroMandateId(params.row.autogiroMandate_id);
    setOpenAddAutogiroDialog(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: t('invoice:autogiro.tableHeader.firstName'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:autogiro.tableHeader.lastName'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName}</>;
      },
    },
    {
      field: 'personalIdentityNumber',
      headerName: t('invoice:autogiro.tableHeader.personalIdentityNumber'),
      headerAlign: 'left',
      sortable: true,
      type: 'number',
      flex: 3,
      renderCell: (params: GridCellParams) => {
        return <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>{params.row.personalIdentityNumber}</Box>;
      },
    },
    {
      field: 'status',
      headerName: t('invoice:autogiro.tableHeader.status'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <AutogiroMandateStatus item={params.row} />
          </Box>
        );
      },
    },
  ];

  return (
    <Box
      className='section-inset'
      sx={{
        mx: -2,
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {},
      }}
      ref={scrollRef}
    >
      <PageFilterSection
        leftContent={
          <SearchField
            initialValue={autogiroMandateSearchFilter.filter || ''}
            onSearch={(newSearchString) => {
              setAutogiroMandateSearchFilter((prev) => ({ ...prev, filter: newSearchString }));
            }}
          />
        }
      />
      <DataGrid<AutogiroMandateResponse>
        columns={columns}
        rows={rows}
        autoHeight
        dataModel={data}
        columnVisibilityModel={{
          autogiroMandate_id: false,
        }}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          const newSort = newSortModel[0]?.sort === 'asc' ? 'desc' : 'asc';
          setSortModel(newSortModel);
          setAutogiroMandateSearchFilter((prev) => ({
            ...prev,
            pageIndex: 0,
            sortCol: newSortModel[0]?.field,
            sortDir: newSort,
          }));
        }}
        onRowClick={onRowClick}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
